import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  OverlayService,
  SignalCheckbox,
  SignalDropdown,
  SignalFormDirective,
  SignalTextField,
} from '@vallift/ngl';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';
import type {
  FormField,
  FormFieldDataLibraryCollectionRef,
} from '../../../../models/built-form';
import {
  LinkCollectionButtonComponent,
  type LinkedCollectionResult,
} from '../../../link-collection-button/link-collection-button';
import {
  CollectionColumnPickerOverlayComponent,
  type CollectionColumnPickerResult,
} from '../../../collection-column-picker-overlay/collection-column-picker-overlay';
import {
  resolveCollectionDropdownItems,
  SEARCHABLE_PICKER_PAGE_SIZE,
  type CollectionSelectOption,
} from '../../../../utils/resolve-collection-dropdown-items';

interface SearchablePickerConfigModel {
  label: string;
  placeholder: string;
  helpText: string;
  defaultValue: string;
  required: boolean;
}

interface ColumnOption {
  key: string;
  label: string;
}

@Component({
  selector: 'app-searchable-picker-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalCheckbox,
    SignalDropdown,
    SignalFormDirective,
    AppSvgIconComponent,
    LinkCollectionButtonComponent,
  ],
  templateUrl: './searchable-picker-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchablePickerConfigViewComponent {
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<SearchablePickerConfigModel>({
    label: '',
    placeholder: '',
    helpText: '',
    defaultValue: '',
    required: false,
  });

  readonly linkedCollection = signal<FormFieldDataLibraryCollectionRef | null>(
    null,
  );
  readonly isCollectionLinked = computed(() => this.linkedCollection() != null);
  readonly collectionColumns = signal<ColumnOption[]>([]);
  readonly collectionDefaultItems = signal<CollectionSelectOption[]>([]);

  readonly displaySummary = computed(() => {
    const linked = this.linkedCollection();
    if (!linked?.displayFieldKey) {
      return '';
    }
    const primary =
      linked.displayFieldLabel ||
      this.collectionColumns().find(
        (column) => column.key === linked.displayFieldKey,
      )?.label ||
      linked.displayFieldKey;
    const secondaryKey = linked.secondaryDisplayFieldKey;
    if (!secondaryKey) {
      return primary;
    }
    const secondary =
      linked.secondaryDisplayFieldLabel ||
      this.collectionColumns().find((column) => column.key === secondaryKey)
        ?.label ||
      secondaryKey;
    return `${primary} · ${secondary}`;
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;
  private lastLoadedCollectionId: string | null = null;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      const collection = next.dataLibraryCollection;
      this.linkedCollection.set(
        collection?.dataLibraryId
          ? {
              dataLibraryId: collection.dataLibraryId,
              displayFieldKey: collection.displayFieldKey,
              displayFieldLabel: collection.displayFieldLabel,
              secondaryDisplayFieldKey: collection.secondaryDisplayFieldKey,
              secondaryDisplayFieldLabel: collection.secondaryDisplayFieldLabel,
              searchFields: collection.searchFields,
              pageSize: collection.pageSize ?? SEARCHABLE_PICKER_PAGE_SIZE,
              label: collection.label,
              key: collection.key,
            }
          : null,
      );
      this.model.set({
        label: next.label,
        placeholder: next.placeholder ?? '',
        helpText: next.helpText ?? '',
        defaultValue:
          typeof next.config?.defaultValue === 'string'
            ? next.config.defaultValue
            : '',
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const linked = this.linkedCollection();
      const collectionId = linked?.dataLibraryId?.trim() ?? '';
      if (!collectionId) {
        this.collectionColumns.set([]);
        this.collectionDefaultItems.set([]);
        this.lastLoadedCollectionId = null;
        return;
      }
      if (collectionId === this.lastLoadedCollectionId) {
        return;
      }
      this.lastLoadedCollectionId = collectionId;
      this.loadCollectionMetadata(collectionId);
    });

    effect((onCleanup) => {
      const linked = this.linkedCollection();
      const collectionId = linked?.dataLibraryId?.trim() ?? '';
      const displayFieldKey = linked?.displayFieldKey?.trim() ?? '';
      if (!collectionId || !displayFieldKey) {
        this.collectionDefaultItems.set([]);
        return;
      }
      const sub = resolveCollectionDropdownItems(
        this.workspaceApi,
        collectionId,
        displayFieldKey,
      ).subscribe({
        next: (items) => {
          this.collectionDefaultItems.set(items);
          const current = this.model().defaultValue.trim();
          if (current && !items.some((item) => item.id === current)) {
            this.model.update((model) => ({ ...model, defaultValue: '' }));
          }
        },
        error: () => this.collectionDefaultItems.set([]),
      });
      onCleanup(() => sub.unsubscribe());
    });

    effect(() => {
      const draft = this.model();
      const linked = this.linkedCollection();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const trimmedDefault = draft.defaultValue.trim();
      const defaultValue = linked ? trimmedDefault || undefined : undefined;
      const primary = linked?.displayFieldKey?.trim();
      const secondary = linked?.secondaryDisplayFieldKey?.trim();
      const searchFields =
        primary && secondary
          ? [primary, secondary]
          : primary
            ? [primary]
            : undefined;

      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        placeholder: draft.placeholder.trim() || undefined,
        helpText: draft.helpText.trim() || undefined,
        options: undefined,
        config: {
          ...field.config,
          defaultValue,
        },
        default: defaultValue
          ? { source: 'static', value: defaultValue }
          : undefined,
        validation: {
          required: draft.required,
        },
        required: draft.required,
        dataLibraryCollection: linked
          ? {
              dataLibraryId: linked.dataLibraryId,
              displayFieldKey: linked.displayFieldKey,
              displayFieldLabel: linked.displayFieldLabel,
              secondaryDisplayFieldKey: linked.secondaryDisplayFieldKey,
              secondaryDisplayFieldLabel: linked.secondaryDisplayFieldLabel,
              searchFields,
              pageSize: SEARCHABLE_PICKER_PAGE_SIZE,
              label: linked.label,
              key: linked.key,
            }
          : undefined,
      });
    });
  }

  onDefaultValueChange(value: unknown): void {
    const next =
      typeof value === 'string' ? value.trim() : value == null ? '' : String(value);
    this.model.update((model) => ({
      ...model,
      defaultValue: next,
    }));
  }

  onCollectionSelected(result: LinkedCollectionResult): void {
    this.suppressEmit = false;
    this.lastLoadedCollectionId = null;
    const primary = result.displayFieldKey.trim();
    const secondary = result.secondaryDisplayFieldKey?.trim() || '';
    this.linkedCollection.set({
      dataLibraryId: result.id,
      displayFieldKey: primary,
      displayFieldLabel: result.displayFieldLabel,
      secondaryDisplayFieldKey: secondary || undefined,
      secondaryDisplayFieldLabel: result.secondaryDisplayFieldLabel,
      searchFields: secondary ? [primary, secondary] : [primary],
      pageSize: SEARCHABLE_PICKER_PAGE_SIZE,
      label: result.name,
      key: result.key,
    });
    this.model.update((model) => ({
      ...model,
      defaultValue: result.defaultItemId?.trim() || '',
    }));
  }

  changeDisplayFields(): void {
    const linked = this.linkedCollection();
    if (!linked?.dataLibraryId) {
      return;
    }
    const dialogRef = this.overlayService.openModal(
      CollectionColumnPickerOverlayComponent,
      {
        fitContent: true,
        panelClass: ['w-[24rem]', 'max-w-[calc(100%-2rem)]'],
        data: {
          collectionId: linked.dataLibraryId,
          collectionName: linked.label,
          selectionMode: 'primarySecondary',
          selectedDisplayFieldKey: linked.displayFieldKey,
          selectedSecondaryDisplayFieldKey: linked.secondaryDisplayFieldKey,
          selectedDefaultItemId: this.model().defaultValue.trim() || undefined,
        },
      },
    );
    dialogRef.closed.subscribe(
      (result: CollectionColumnPickerResult | null | undefined) => {
        if (
          !result?.displayFieldKey ||
          !result.secondaryDisplayFieldKey?.trim()
        ) {
          return;
        }
        const primary = result.displayFieldKey.trim();
        const secondary = result.secondaryDisplayFieldKey.trim();
        this.linkedCollection.update((current) =>
          current
            ? {
                ...current,
                displayFieldKey: primary,
                displayFieldLabel: result.displayFieldLabel,
                secondaryDisplayFieldKey: secondary,
                secondaryDisplayFieldLabel: result.secondaryDisplayFieldLabel,
                searchFields: [primary, secondary],
                pageSize: SEARCHABLE_PICKER_PAGE_SIZE,
              }
            : current,
        );
        this.model.update((model) => ({
          ...model,
          defaultValue: result.defaultItemId?.trim() || '',
        }));
      },
    );
  }

  removeLinkedCollection(): void {
    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionDefaultItems.set([]);
    this.lastLoadedCollectionId = null;
    this.model.update((model) => ({
      ...model,
      defaultValue: '',
    }));
  }

  private loadCollectionMetadata(collectionId: string): void {
    this.workspaceApi.getDataLibraryCollection(collectionId).subscribe({
      next: (response) => {
        const data = response.data;
        if (!data || this.linkedCollection()?.dataLibraryId !== collectionId) {
          return;
        }
        const columns = (data.fields ?? []).map(
          (field: DataLibraryCollectionFieldResponse): ColumnOption => ({
            key: field.key,
            label: field.name || field.key,
          }),
        );
        this.collectionColumns.set(columns);

        const linked = this.linkedCollection();
        const primaryKey = linked?.displayFieldKey?.trim();
        const secondaryKey = linked?.secondaryDisplayFieldKey?.trim();
        let next = linked;
        if (primaryKey && !linked?.displayFieldLabel) {
          const label = columns.find((column) => column.key === primaryKey)
            ?.label;
          if (label && next) {
            next = { ...next, displayFieldLabel: label };
          }
        }
        if (secondaryKey && !linked?.secondaryDisplayFieldLabel) {
          const label = columns.find((column) => column.key === secondaryKey)
            ?.label;
          if (label && next) {
            next = { ...next, secondaryDisplayFieldLabel: label };
          }
        }
        if (next && next !== linked) {
          this.suppressEmit = true;
          this.linkedCollection.set(next);
        }
      },
      error: () => {
        if (this.linkedCollection()?.dataLibraryId === collectionId) {
          this.collectionColumns.set([]);
        }
      },
    });
  }
}
